import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CreditGrant } from "@/components/admin/CreditGrant";
import { ShieldCheck, Search, Lock, Cpu, Zap, Users, Timer, Activity, KeyRound, Loader2 } from "lucide-react";
import { Header } from "@/pages/Operations";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PaymentsPanel } from "@/components/admin/PaymentsPanel";
import { KanbanBoard } from "@/components/admin/KanbanBoard";
import { EvidencePanel } from "@/components/admin/EvidencePanel";
import { RenderOpsPanel } from "@/components/admin/RenderOpsPanel";
import { DemoResetCard } from "@/components/admin/DemoResetCard";

const ROLES = ["founder", "admin", "viewer"];
const roleColor = {
  founder: "text-amber-300 bg-amber-500/15 border-amber-500/30",
  admin: "text-purple-300 bg-purple-500/15 border-purple-500/30",
  "lead-architect": "text-cyan-300 bg-cyan-500/15 border-cyan-500/30",
  creator: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30",
  viewer: "text-slate-300 bg-white/5 border-white/10",
};
const jobStatusColor = {
  Rendering: "text-cyan-300 bg-cyan-500/10", Queued: "text-slate-300 bg-white/5",
  Completed: "text-emerald-300 bg-emerald-500/10", Failed: "text-red-300 bg-red-500/10",
};
const loadColor = { High: "text-amber-400", Normal: "text-emerald-400", Critical: "text-red-400", Healthy: "text-emerald-400", Idle: "text-slate-400", Failing: "text-red-400" };
const fmtDay = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const todayISO = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10);

export default function Admin() {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [claimPw, setClaimPw] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [channels, setChannels] = useState([]);
  const loadChannels = () => api.get("/faceless/admin/channels").then((r) => setChannels(r.data)).catch(() => {});
  const approveChannel = async (c) => {
    try { await api.put(`/faceless/channels/${c.channel_id}/approve`); toast.success(`"${c.name || c.niche}" approved for ${c.owner_email || "owner"}`); loadChannels(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approval failed"); }
  };
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [denied, setDenied] = useState(false);
  const [expiry, setExpiry] = useState({});

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); });
    load(); loadChannels();
  }, []);

  const toggleWhitelist = async (u) => {
    const enable = !u.whitelisted;
    const expires_at = enable ? expiry[u.user_id] || null : null;
    try {
      await api.patch(`/admin/users/${u.user_id}/whitelist`, { whitelisted: enable, note: "Complimentary access", expires_at });
      toast.success(enable ? `${u.name} whitelisted — free usage${expires_at ? ` until ${fmtDay(expires_at)}` : ""}` : `${u.name} removed from whitelist`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const claimAdmin = async (e) => {
    e.preventDefault(); setClaiming(true);
    try {
      const r = await api.post("/auth/claim-admin", { password: claimPw });
      setUser(r.data); setDenied(false); setClaimPw("");
      toast.success("You are now Founder — full admin + creator rights");
      api.get("/admin/stats").then((s) => setStats(s.data)).catch(() => {}); load(); loadChannels();
    } catch (err) { toast.error(err?.response?.data?.detail || "Unlock failed"); }
    finally { setClaiming(false); }
  };
  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/admin/users/${uid}/role`, { role });
      toast.success(`Role updated to ${role}`);
      setUsers((us) => us.map((u) => (u.user_id === uid ? { ...u, role } : u)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  if (denied) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center mt-20">
        <div className="w-16 h-16 rounded-2xl bg-red-500/15 flex items-center justify-center mx-auto mb-4"><Lock size={28} className="text-red-400" /></div>
        <h2 className="font-display text-2xl font-bold">Access Restricted</h2>
        <p className="text-slate-400 mt-2 text-sm">Command Center requires Founder or Admin privileges. Your role is <span className="text-purple-300 font-mono">{user?.role}</span>.</p>
        <div data-testid="owner-unlock" className="glass rounded-2xl p-5 mt-8 text-left">
          <div className="font-display font-semibold text-sm flex items-center gap-2"><KeyRound size={15} className="text-amber-400" /> I am the owner</div>
          <p className="text-xs text-slate-500 mt-1">Enter the admin password once. This account ({user?.email}) becomes Founder permanently — full admin rights and unmetered creator access.</p>
          <form onSubmit={claimAdmin} className="flex gap-2 mt-3">
            <input data-testid="owner-unlock-password" type="password" value={claimPw} onChange={(e) => setClaimPw(e.target.value)} placeholder="Admin password" className="flex-1 bg-[#0A0C14] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500/40" />
            <button data-testid="owner-unlock-submit" disabled={claiming || !claimPw} className="px-4 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-amber-500 to-orange-600 text-white disabled:opacity-50 inline-flex items-center gap-1">{claiming ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />} Unlock admin</button>
          </form>
        </div>
      </div>
    );
  }

  const filtered = users.filter((u) => u.name?.toLowerCase().includes(query.toLowerCase()) || u.email?.toLowerCase().includes(query.toLowerCase()));

  const cards = stats ? [
    { label: "Rendering Now", value: stats.active_render_jobs?.toLocaleString(), sub: `${stats.renders_24h ?? 0} renders in 24h`, delta: `${stats.completed_videos ?? 0} done`, icon: Zap, color: "text-cyan-400" },
    { label: "Render Success", value: `${stats.gpu_utilization}%`, sub: `${stats.failed_videos ?? 0} failed / ${stats.total_videos ?? 0} total`, delta: "", icon: Cpu, color: "text-purple-400" },
    { label: "Total Users", value: stats.total_users?.toLocaleString(), sub: `${stats.whitelisted_users ?? 0} whitelisted`, delta: "", icon: Users, color: "text-emerald-400" },
    { label: "Revenue (approved UPI)", value: `₹${(stats.revenue_inr ?? 0).toLocaleString("en-IN")}`, sub: `${stats.pending_orders ?? 0} orders pending`, delta: "", icon: Timer, color: "text-amber-400" },
  ] : [];

  return (
    <div data-testid="admin-panel-master-dashboard" className="p-5 sm:p-8 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-1">
        <Header icon={ShieldCheck} eyebrow="Command Center · RBAC" title="System Dashboard" subtitle="Real-time metrics for the Vectra render engine and platform." />
        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-xs">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /><span className="text-emerald-300 font-mono">ALL SYSTEMS NORMAL</span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            data-testid={`admin-stat-${i}`} className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2"><c.icon size={19} className={c.color} /><span className={`text-[10px] font-mono ${c.delta.startsWith("-") ? "text-red-400" : "text-emerald-400"}`}>{c.delta}</span></div>
            <div className="text-2xl font-display font-extrabold font-mono">{c.value ?? "—"}</div>
            <div className="text-xs text-slate-500 mt-1">{c.label}</div>
            <div className="text-[10px] text-slate-600 mt-0.5">{c.sub}</div>
          </motion.div>
        ))}
      </div>

      <KanbanBoard />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 glass rounded-2xl p-5">
          <div className="font-display font-semibold text-sm mb-3">Recent Render Jobs</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] font-mono uppercase text-slate-500 border-b border-white/10">
                <th className="text-left py-2">Job ID</th><th className="text-left py-2">User</th><th className="text-left py-2">Status</th><th className="text-left py-2">Progress</th><th className="text-left py-2 hidden sm:table-cell">Engine</th>
              </tr></thead>
              <tbody>
                {(stats?.render_jobs || []).map((j, i) => (
                  <tr key={i} data-testid={`render-job-${i}`} className="border-b border-white/5">
                    <td className="py-2.5 font-mono text-xs text-purple-300">{j.job_id}</td>
                    <td className="py-2.5 text-xs text-slate-400 truncate max-w-[120px]">{j.user}</td>
                    <td className="py-2.5"><span className={`text-[10px] font-mono px-2 py-0.5 rounded ${jobStatusColor[j.status]}`}>{j.status}</span></td>
                    <td className="py-2.5"><div className="flex items-center gap-2"><div className="w-20 h-1.5 rounded-full bg-white/5"><div className={`h-full rounded-full ${j.status === "Failed" ? "bg-red-500" : "bg-gradient-to-r from-purple-500 to-cyan-500"}`} style={{ width: `${j.progress}%` }} /></div><span className="text-[10px] font-mono text-slate-500">{j.progress}%</span></div></td>
                    <td className="py-2.5 text-xs text-slate-400 hidden sm:table-cell">{j.engine}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="glass rounded-2xl p-5">
          <div className="font-display font-semibold text-sm mb-3">Render Engine Health <span className="text-[10px] font-mono text-slate-500 ml-2">completed / failed per engine</span></div>
          <div className="space-y-3">
            {(stats?.gpu_nodes || []).map((n, i) => (
              <div key={i} data-testid={`gpu-node-${i}`} className="flex items-center justify-between">
                <div><div className="font-mono text-xs">{n.region}</div><div className="text-[10px] text-slate-600">{n.gpu}</div></div>
                <div className="text-right"><div className={`text-sm font-bold font-mono ${loadColor[n.level]}`}>{n.load} ok · {n.failed} failed</div><div className="text-[10px] text-slate-600">{n.level}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PaymentsPanel />
      <DemoResetCard />
      <RenderOpsPanel />
      <EvidencePanel />

      <div className="glass rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-white/10 flex items-center gap-2">
          <Search size={15} className="text-slate-500" />
          <input data-testid="admin-user-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search users & quotas..." className="bg-transparent text-sm outline-none flex-1 placeholder:text-slate-600" />
          <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1"><Activity size={12} /> {users.length} users</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-white/10">
              <th className="text-left px-4 py-3">User</th><th className="text-left px-4 py-3 hidden sm:table-cell">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Assign</th><th className="text-left px-4 py-3">Free usage</th>
            </tr></thead>
            <tbody>
              {filtered.map((u, i) => (
                <tr key={u.user_id} data-testid={`user-row-${i}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3"><div className="flex items-center gap-3">{u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-cyan-500" />}<span className="font-medium">{u.name}</span></div></td>
                  <td className="px-4 py-3 text-slate-500 hidden sm:table-cell font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono uppercase px-2 py-1 rounded-md border ${roleColor[u.role] || roleColor.viewer}`}>{u.role}</span></td>
                  <td className="px-4 py-3">
                    <select data-testid={`role-select-${i}`} value={u.role} onChange={(e) => changeRole(u.user_id, e.target.value)} className="bg-[#0A0C14] border border-white/10 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-purple-500/40">
                      {!ROLES.includes(u.role) && <option value={u.role} disabled className="bg-[#0E1019]">{u.role} (whitelist-gated)</option>}
                      {ROLES.map((r) => <option key={r} className="bg-[#0E1019]">{r}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {u.whitelisted ? (
                      <div className="flex items-center gap-2">
                        <span data-testid={`whitelist-status-${i}`} className="text-[10px] font-mono px-2 py-1 rounded-md border border-emerald-500/40 bg-emerald-500/15 text-emerald-200">
                          WHITELISTED{u.whitelist_expires_at ? ` · until ${fmtDay(u.whitelist_expires_at)}` : " · no expiry"}
                        </span>
                        <button data-testid={`whitelist-toggle-${i}`} onClick={() => toggleWhitelist(u)} className="text-[10px] font-mono px-2 py-1 rounded-md border border-white/10 text-slate-500 hover:text-red-300 hover:border-red-500/40">Remove</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input type="date" data-testid={`whitelist-expiry-${i}`} min={todayISO()} value={expiry[u.user_id] || ""} onChange={(e) => setExpiry({ ...expiry, [u.user_id]: e.target.value })}
                          title="Optional end date — leave blank for no expiry" className="bg-[#0A0C14] border border-white/10 rounded-lg px-2 py-1 text-[10px] font-mono text-slate-300 outline-none focus:border-purple-500/40 [color-scheme:dark]" />
                        <button data-testid={`whitelist-toggle-${i}`} onClick={() => toggleWhitelist(u)} className="text-[10px] font-mono px-2 py-1 rounded-md border border-white/10 text-slate-500 hover:text-white">Whitelist</button>
                      </div>
                    )}
                    <CreditGrant user={u} index={i} onDone={load} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div data-testid="admin-channel-approvals" className="glass rounded-2xl overflow-hidden mt-6">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="font-display font-semibold text-sm">Faceless channel approvals <span className="text-[10px] font-mono text-slate-500 ml-2">{channels.filter((c) => !c.approved).length} pending</span></div>
          <button data-testid="admin-channels-refresh" onClick={loadChannels} className="text-[10px] font-mono text-slate-500 hover:text-white">refresh</button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] font-mono uppercase text-slate-500 border-b border-white/10"><th className="text-left px-4 py-2">Channel</th><th className="text-left px-4 py-2 hidden sm:table-cell">Owner</th><th className="text-left px-4 py-2">Platform</th><th className="text-left px-4 py-2">Status</th><th /></tr></thead>
          <tbody>
            {channels.map((c, i) => (
              <tr key={c.channel_id} data-testid={`admin-channel-${i}`} className="border-b border-white/5">
                <td className="px-4 py-2.5">{c.name || c.niche}<div className="text-[10px] text-slate-500">{c.niche}</div></td>
                <td className="px-4 py-2.5 text-xs text-slate-400 hidden sm:table-cell">{c.owner_email}</td>
                <td className="px-4 py-2.5 text-xs">{c.platform} · {c.language}</td>
                <td className="px-4 py-2.5"><span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${c.approved ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>{c.approved ? "APPROVED" : "PENDING"}</span></td>
                <td className="px-4 py-2.5 text-right">{!c.approved && <button data-testid={`admin-channel-approve-${i}`} onClick={() => approveChannel(c)} className="text-[10px] font-mono px-2 py-1 rounded-md border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10">Approve</button>}</td>
              </tr>
            ))}
            {!channels.length && <tr><td colSpan={5} className="px-4 py-5 text-xs text-slate-500 text-center">No faceless channels yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
